import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { cn } from '@/lib/utils';
import { CheckCircle, XCircle } from 'lucide-react';
import {
  useCommunityProfile,
  useUpdateCommunityProfile,
  useCheckDisplayNameAvailability,
} from '@/hooks/use-community';

export function CommunitySettingsForm() {
  const { toast } = useToast();
  const { data: profile, isLoading } = useCommunityProfile();
  const updateProfile = useUpdateCommunityProfile();

  const [displayName, setDisplayName] = useState('');
  const [badge, setBadge] = useState('');
  const [showSchool, setShowSchool] = useState(true);
  const [showRealName, setShowRealName] = useState(false);
  // Snapshot of saved values for dirty-tracking
  const [savedData, setSavedData] = useState({ displayName: '', badge: '', showSchool: true, showRealName: false });
  const initializedRef = useRef(false);

  // Sync form state only on initial profile load (not after mutation refetches)
  useEffect(() => {
    if (profile && !initializedRef.current) {
      initializedRef.current = true;
      const dn = profile.display_name || '';
      const b = profile.badge || '';
      const ss = profile.show_school ?? true;
      const srn = profile.show_real_name_in_groups ?? false;
      setDisplayName(dn);
      setBadge(b);
      setShowSchool(ss);
      setShowRealName(srn);
      setSavedData({ displayName: dn, badge: b, showSchool: ss, showRealName: srn });
    }
  }, [profile]);

  const { isAvailable, isChecking } = useCheckDisplayNameAvailability(displayName, savedData.displayName);

  const isDirty =
    displayName.trim() !== savedData.displayName ||
    badge !== savedData.badge ||
    showSchool !== savedData.showSchool ||
    showRealName !== savedData.showRealName;

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmed = displayName.trim();
    if (!trimmed) {
      toast({ title: 'Display name required', description: 'Please enter a display name.', variant: 'destructive' });
      return;
    }
    if (trimmed.length > 30) {
      toast({ title: 'Too long', description: 'Display name must be 30 characters or less.', variant: 'destructive' });
      return;
    }

    try {
      await updateProfile.mutateAsync({
        display_name: trimmed,
        badge: badge || null,
        show_school: showSchool,
        show_real_name_in_groups: showRealName,
      });
      setSavedData({ displayName: trimmed, badge, showSchool, showRealName });
      toast({ title: 'Settings saved', description: 'Your community settings have been updated.' });
    } catch (error: unknown) {
      const isUniquenessViolation = (error as { code?: string })?.code === '23505';
      toast({
        title: isUniquenessViolation ? 'Display name taken' : 'Error',
        description: isUniquenessViolation
          ? 'This display name is already taken. Please choose a different name.'
          : 'Failed to save community settings. Please try again.',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center p-4">
        <LoadingSpinner className="h-6 w-6" />
      </div>
    );
  }

  if (!profile) {
    return (
      <p className="text-sm text-muted-foreground py-4">
        Visit the Community page first to create your community profile.
      </p>
    );
  }

  return (
    <form onSubmit={handleSave} className="space-y-5">
      {/* Display Name */}
      <div className="space-y-2">
        <Label htmlFor="displayName" className="text-gray-700 dark:text-gray-300">Display Name</Label>
        <Input
          id="displayName"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="Your public display name"
          maxLength={30}
          className="border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-orange-500 dark:focus:ring-orange-400"
        />
        <p className="text-xs text-muted-foreground">
          This is your anonymous name on discussion posts and comments.
        </p>
        {isChecking && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <LoadingSpinner className="h-3.5 w-3.5" />
            Checking availability...
          </div>
        )}
        {!isChecking && isAvailable === true && (
          <div className="flex items-center gap-1.5 text-xs text-green-600 dark:text-green-400">
            <CheckCircle className="h-3.5 w-3.5" />
            Display name is available
          </div>
        )}
        {!isChecking && isAvailable === false && (
          <div className="flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400">
            <XCircle className="h-3.5 w-3.5" />
            Display name is already taken
          </div>
        )}
      </div>

      {/* Journey Badge */}
      <div className="space-y-2">
        <Label htmlFor="badge" className="text-gray-700 dark:text-gray-300">Where are you in your journey?</Label>
        <select
          id="badge"
          value={badge}
          onChange={(e) => setBadge(e.target.value)}
          className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm min-h-[44px] md:min-h-[36px] focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-400"
        >
          <option value="">None</option>
          <option value="1L">1L</option>
          <option value="2L">2L</option>
          <option value="3L">3L</option>
          <option value="JD">JD</option>
          <option value="Bar Prep">Bar Prep</option>
          <option value="JD Candidate">JD Candidate</option>
          <option value="Attorney">Attorney</option>
        </select>
        <p className="text-xs text-muted-foreground">
          This badge appears next to your name on community posts and comments.
        </p>
      </div>

      {/* School Visibility Toggle */}
      <div className="space-y-1.5">
        <Label className="text-gray-700 dark:text-gray-300">Discussions: School Visibility</Label>
        <div>
          <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 p-0.5">
            <button
              type="button"
              onClick={() => setShowSchool(true)}
              className={cn(
                'px-3 py-1.5 text-sm font-medium rounded-md transition-all',
                showSchool
                  ? 'bg-white dark:bg-gray-950 text-[#F37022] shadow-md font-bold'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              )}
            >
              Show School
            </button>
            <button
              type="button"
              onClick={() => setShowSchool(false)}
              className={cn(
                'px-3 py-1.5 text-sm font-medium rounded-md transition-all',
                !showSchool
                  ? 'bg-white dark:bg-gray-950 text-[#F37022] shadow-md font-bold'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              )}
            >
              Hide School
            </button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          When visible, your law school appears as a badge on your community profile.
        </p>
      </div>

      {/* Study Group Name Visibility Toggle */}
      <div className="space-y-1.5">
        <Label className="text-gray-700 dark:text-gray-300">Study Groups: Name Visibility</Label>
        <div>
          <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 p-0.5">
            <button
              type="button"
              onClick={() => setShowRealName(true)}
              className={cn(
                'px-3 py-1.5 text-sm font-medium rounded-md transition-all',
                showRealName
                  ? 'bg-white dark:bg-gray-950 text-[#F37022] shadow-md font-bold'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              )}
            >
              Show Name
            </button>
            <button
              type="button"
              onClick={() => setShowRealName(false)}
              className={cn(
                'px-3 py-1.5 text-sm font-medium rounded-md transition-all',
                !showRealName
                  ? 'bg-white dark:bg-gray-950 text-[#F37022] shadow-md font-bold'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              )}
            >
              Hide Name
            </button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          When enabled, study group members can see your real name. Your real name will never be exposed to the Community Discussions.
        </p>
      </div>

      {(isDirty || updateProfile.isPending) && (
        <Button
          type="submit"
          disabled={updateProfile.isPending || isAvailable === false || isChecking}
          className="bg-orange-500 hover:bg-orange-600 text-white"
        >
          {updateProfile.isPending ? (
            <>
              <LoadingSpinner className="mr-2 h-4 w-4" />
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </Button>
      )}
    </form>
  );
}
